import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import uniqBy from 'lodash/uniqBy';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { KEY_ADS_POSITION } from 'src/components/ads/dynamicAds';
import { APP_URL, IS_NO_ROBOT_INDEX, SHOW_COMMEMORATE } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { IAds } from 'src/modules/ads';
import { Category, Datum, Links, Meta, SubCategory2 } from 'src/modules/cid';
import { DataContent, IAffiliate } from 'src/modules/cid/id';
import { Data } from 'src/modules/sections/section-1';
import { NAV_DATA } from 'src/redux/navSlice';
import { useAppDispatch, useAppSelector } from 'src/redux/store';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { convertObjPath, escapeChar, rewriteTxt, timestamp } from 'src/utils/helper';
import { GET, GET_ARTICLES } from 'src/utils/service';
import useUser from 'src/utils/useUser';
import styled from 'styled-components';

const CategoryAudio = dynamic(import('src/components/category/category-audio'));
const CategoryDefault = dynamic(import('src/components/category/category-default'));
const CategoryVideo = dynamic(import('src/components/category/category-video'));
const Layout = dynamic(import('src/components/layouts'));
const PageError = dynamic(import('pages/404'));
const SectionAffiliate = dynamic(import('src/components/sections/section-events/section-affiliate'));
const DynamicAds = dynamic(import('src/components/ads/dynamicAds'));

type Props = {
  articles: Datum[];
  articlesHighlight: Data;
  acticlesresDataPin: DataContent;
  nextArticles: Links;
  category: Category;
  subCategory: SubCategory2;
  meta: Meta;
  lockCategory: any;
  dataSetPinEmbed: string | any;
  navBar?: any;
  affiliate: IAffiliate[];
  ads?: IAds[];
};

const PageCategory = ({ articles, articlesHighlight, acticlesresDataPin, nextArticles, category, subCategory, meta, lockCategory, dataSetPinEmbed, navBar, affiliate, ads }: Props) => {
  const data = useUser();
  const sessionSevId = data?.user ? decryptionClient(data?.user) : null;
  if (isEmpty(articles)) return <PageError sessionSevId={sessionSevId} navBar={navBar} />;
  const router: any = useRouter();
  const navSubMenu = useAppSelector(props => props?.navData?.result?.[0]);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const find = navBar && navBar?.filter((i: any) => i.nameEng === router?.query?.cid);
    if (!isEmpty(find)) {
      dispatch(NAV_DATA(find));
    }
  }, []);

  // check ID
  const articlesFilter = articles.filter(i => i.id !== acticlesresDataPin?.id);

  // slicID
  const sliceId = !isEmpty(acticlesresDataPin) ? 0 : 1;

  const lockCategoryOne = !isEmpty(lockCategory?.dataSet) && lockCategory?.dataSet[0]?.value;
  const lockCategoryTwo = !isEmpty(lockCategory?.dataSet) && lockCategory?.dataSet[1]?.value;

  const _article = {
    title: meta?.title || WEB_TITLE,
    description: meta?.description || WEB_DESC,
    keyword: meta?.keyword || WEB_KEYWORD
  };

  const articleDescriptionSeo = escapeChar(_article?.description) || WEB_DESC;
  let setItemListElement = '';
  let baseUrlCategory = `${APP_URL}/category/${category?.en}`;

  /* -------------------------------- START : LOADMORE -------------------------------- */
  const nextLinkRef = useRef(nextArticles);
  const listArticlesRef = useRef(articlesFilter);
  const [listArticles, setListArticles] = useState(articlesFilter);
  // const [isFetching, setIsFetching] = useState(false);
  // const countRef = useRef(0);
  // const sectionRef = useRef(1);
  const handleLoad = async () => {
    try {
      if (nextArticles) {
        // console.log('#3', nextLinkRef.current);
        const resData = await GET_ARTICLES(`${nextLinkRef.current || ''}`, false, true);
        if (!isEmpty(resData?.data)) {
          nextLinkRef.current = resData?.nextTo || resData?.links?.next;
          const newLists = [...listArticlesRef.current, ...resData?.data];
          listArticlesRef.current = uniqBy(newLists, 'id');

          setListArticles(listArticlesRef.current);
        }
      }
    } catch (err) {
      console.error(`${timestamp()} ==========> LOAD_CATEGORY_LIST ERROR : ${nextLinkRef?.current}`, err);
    }
  };

  if (subCategory.en != null) {
    baseUrlCategory = `${APP_URL}/category/${category?.en}/${subCategory?.en}`;
    setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "${category?.th}",
          "item": "${APP_URL}/category/${category?.en}"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "${subCategory?.th}",
          "item": "${baseUrlCategory}"
        }
      ]`;
  } else {
    baseUrlCategory = `${APP_URL}/category/${category?.en}`;
    setItemListElement = `[
        {
          "@type": "ListItem",
          "position": 1,
          "name": "หน้าแรก",
          "item": "${APP_URL}"
        },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "${category?.th}",
            "item": "${APP_URL}/category/${category?.en}"
          }
        ]`;
  }

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={_article?.title || WEB_TITLE}
        description={_article?.description || WEB_DESC}
        openGraph={{
          type: 'article',
          locale: 'en_US',
          site_name: WEB_NAME,
          title: _article?.title || WEB_TITLE,
          description: _article?.description || WEB_DESC,
          url: baseUrlCategory,
          images: [
            {
              url: IMAGE_DEFAULT,
              width: 1920,
              height: 1080,
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={_article?.title || WEB_TITLE} />
        <meta property='twitter:description' content={_article?.description || WEB_DESC} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        {/* ------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
              __th_page="${subCategory?.en ? subCategory.en : category?.en}";
            `
          }}
        />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "${WEB_NAME}",
                "url": "${WEB_URL.WEB}",
                "description": "${articleDescriptionSeo}",
                "sameAs": [
                  "${WEB_URL.FACEBOOK}",
                  "${WEB_URL.YOUTUBE}",
                  "${WEB_URL.INSTAGRAM}",
                  "${WEB_URL.TWITTER}"
                ],
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": "${WEB_URL.WEB}search?q={search_term_string}",
                  "query-input": "required name=search_term_string"
                }
              }
            `
          }}
        />

        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "WebPage",
                  "name": "${subCategory?.th ? subCategory.th : category?.th}",
                  "description": "${articleDescriptionSeo}",
                  "publisher": {
                    "@type": "ProfilePage",
                    "name": "${WEB_NAME}"
                  }
                }
              `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "http://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": ${setItemListElement}
              }
            `
          }}
        />
        {/* <img
          width='99999'
          height='99999'
          alt='svasds'
          title='svasds'
          className='svgll'
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '99vw',
            height: '99vh',
            maxWidth: '99vw',
            maxHeight: '99vh',
            overflow: 'hidden'
          }}
          src='data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIHdpZHRoPSI5OTk5OXB4IiBoZWlnaHQ9Ijk5OTk5cHgiIHZpZXdCb3g9IjAgMCA5OTk5OSA5OTk5OSIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIj48ZyBzdHJva2U9Im5vbmUiIGZpbGw9Im5vbmUiIGZpbGwtb3BhY2l0eT0iMCI+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9Ijk5OTk5IiBoZWlnaHQ9Ijk5OTk5Ij48L3JlY3Q+IDwvZz4gPC9zdmc+'
        ></img> */}
      </Head>
      {!isEmpty(affiliate) && <SectionAffiliate position={4} data={affiliate} />}
      {/* -------------------------------------------------------------------------- */}
      <Layout sessionSevId={sessionSevId} data={{ header: navBar, footer: navBar }}>
        <CategoryWrapper id='category-wrapper' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
          <InfiniteScroll
            className='infinite-scroll'
            pageStart={0}
            loadMore={handleLoad}
            hasMore={true}
            loader={<div> </div>}
            threshold={2000}
          // useWindow={false}
          >
            {router.query.cid === (lockCategoryOne || 'watch') && (
              <CategoryVideo
                articles={articles}
                articlesHighlight={articlesHighlight}
                acticlesresDataPin={acticlesresDataPin}
                dataSetPinEmbed={dataSetPinEmbed}
                category={category}
                subCategory={subCategory}
                subMenu={navSubMenu}
                sliceId={sliceId}
                listArticles={listArticles}
                // isFetching={isFetching}
                ads={ads}
              />
            )}

            {router.query.cid === (lockCategoryTwo || 'listen') && (
              <CategoryAudio
                articles={articles}
                articlesHighlight={articlesHighlight}
                acticlesresDataPin={acticlesresDataPin}
                dataSetPinEmbed={dataSetPinEmbed}
                category={category}
                subCategory={subCategory}
                subMenu={navSubMenu}
                sliceId={sliceId}
                listArticles={listArticles}
                // isFetching={isFetching}
                ads={ads}
              />
            )}

            {router.query.cid !== (lockCategoryOne || 'watch') && router.query.cid !== (lockCategoryTwo || 'listen') && (
              <CategoryDefault
                navBar={navBar}
                articles={articles}
                articlesHighlight={articlesHighlight}
                acticlesresDataPin={acticlesresDataPin}
                category={category}
                subCategory={subCategory}
                subMenu={navSubMenu}
                sliceId={sliceId}
                listArticles={listArticles}
                // isFetching={isFetching}
                ads={ads}
              />
            )}
          </InfiniteScroll>
        </CategoryWrapper>
        {/* Ads-PPN POSTARTICLE */}
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
      </Layout>
    </>
  );
};

const CategoryWrapper = styled.div``;

export async function getServerSideProps({ res, query, req }: any) {
  const initProps: any = {
    statusCode: 200,
    errMessage: null,
    articles: null,
    articlesHighlight: {},
    category: null,
    subCategory: null,
    meta: null,
    nextArticles: null,
    queryCategory: null,
    lockCategory: null,
    acticlesresDataPin: null,
    dataSetPinEmbed: null,
    navBar: [],
    affiliate: [],
    ads: []
  };

  const payload = {
    block1: 0,
    block2: 0,
    block3: 0,
    headerLatestNews: 5
  };

  initProps.queryCategory = query;
  let uri = `categories/${rewriteTxt(query?.cid)}`;
  if (query?.sub) {
    if (rewriteTxt(query?.sub) === 'latest-news') {
      uri = `categories/${rewriteTxt(query?.cid)}`;
    } else if (rewriteTxt(query?.cid) === 'latest-news') {
      uri = `categories/${rewriteTxt(query?.cid)}`;
    } else {
      uri = `categories/${rewriteTxt(query?.cid)}/${query?.sub}`;
    }
  }
  const regex = /([ก-๙])/;
  if (regex.test(query?.cid)) {
    return {
      redirect: {
        destination: 'news',
        permanent: false
      }
    };
  }

  try {
    const fetchMenuNav = await GET('/api/v1.0/navigations/menu-nav?limit=6');
    if (!isEmpty(fetchMenuNav)) {
      initProps.navBar = fetchMenuNav;
    }
    const regex = /([ก-๙])/;
    if (regex.test(query?.cid)) {
      res.setHeader('location', '/category/latest-news');
      res.statusCode = 301;
      res.end();
    }

    const resData = await GET_ARTICLES(`/api/v1.0/${uri}`, false, true);

    let resDataTo = null;
    if (resData?.statusCode === 404 || isUndefined(resData)) {
      res.setHeader('location', '/');
      res.statusCode = 301;
      res.end();
      // return {
      //   notFound: true
      // };
    }
    if (!isEmpty(resData)) {
      if (isEmpty(resData?.data)) {
        resDataTo = await GET_ARTICLES(resData?.nextTo, false, true);
        initProps.articles = resDataTo?.data || [];
      } else {
        initProps.articles = resData?.data || [];
      }
      initProps.nextArticles = resData?.nextTo || resData?.links?.next || null;
      initProps.category = resData?.category || {};
      initProps.subCategory = resData?.subCategory || {};
      initProps.meta = resData?.meta || {};
      initProps.affiliate = resData?.affiliate || [];
      initProps.ads = resData?.ads || [];
    }

    const fetchSectionOne = await GET(`/api/v1.0/home/section-one${convertObjPath(payload)}`);
    if (!isEmpty(fetchSectionOne)) {
      initProps.articlesHighlight = fetchSectionOne;
    }
    const fetchDataSet = await GET('/api/datasets');

    if (!isEmpty(fetchDataSet)) {
      const dataSets = fetchDataSet; // default datasets
      initProps.lockCategory = find(dataSets, { topic: 'lock-category-video-audio' }) || [];

      const findData = find(dataSets, { topic: 'lock-pin' }) || [];
      for (let i = 0; i < findData?.dataSet?.length; i++) {
        const element = findData?.dataSet[i]?.key === `lock-${query.cid}`;
        const elementEmbed = findData?.dataSet[i]?.key === `embed-${query.cid}`;
        if (element) {
          const dataId = findData?.dataSet[i]?.value;
          const resDataPinId = `/api/v1.0/contents/${dataId}`;
          const resDataPin = await GET(resDataPinId);
          if (!isEmpty(resDataPin)) {
            initProps.acticlesresDataPin = resDataPin;
          }
        }
        if (elementEmbed) {
          initProps.dataSetPinEmbed = findData?.dataSet[i]?.value || [];
        }
      }
    }
  } catch (err: any) {
    console.error(`${timestamp()} ==========> CATEGORY_LIST ERROR : `, err.message);
    initProps.errMessage = err?.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return {
    props: initProps
  };
}

export default PageCategory;
